/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import { getSnapshot } from 'mobx-state-tree';
import { createApiError } from './util/ErrorUtils';
import { parseFieldIDForExpression } from 'components/startingPoints/relationshipGraph/ScopeModel';

/**
 * a set of APIs for starting point operations
 * @module StartingPointsAPI
 */
export default class StartingPointsAPI {
	constructor(startingPointsModel) {

		/**
		 * To be invoked by the product only.
		 */
		this.destroy = function() {
			startingPointsModel = null;
		};
		/**
		 * @return {string} the active data source id
		 */
		this.getActiveDatasourceId = function() {
			return startingPointsModel.exploreVM.activeDataSourceId;
		};

		this.getFieldsByModelId = function(modelId) {
			if (startingPointsModel) {
				return startingPointsModel.fields.fields.get(modelId);
			}
			return Promise.reject( new Error('No StartingPoints Model'));
		};
		/**;
		 * @return {object} - current snapshot of starting point model if it exists; or rejects with an error if starting point model does not exist.
		 */
		this.getState = function() {
			if( startingPointsModel ) {
				return getSnapshot(startingPointsModel);
			} else {
				throw new Error('No StartingPoints Model');
			}
		};

		/**
		 * @return {object} - a promise resolves when relationship data has been loaded
		 */
		this.whenRelationshipDataReady = function() {
			if ( startingPointsModel ) {
				return startingPointsModel.relationship.whenRelationshipDataReady();
			} else {
				return Promise.reject( new Error('No StartingPoints Model'));
			}
		};

		/**
		 * @param {array} [fields] - an array of column ids
		 * @returns a promise resolves with widgetSpecs
		 */
		this.getRecommendations = function(fields) {
			const smartsRecommender = startingPointsModel.exploreVM.relatedRecommendationVM.recommender;
			if (fields) {
				return smartsRecommender.recommendRelatedVisualizationsAsWidgetSpecs(this.getActiveDatasourceId(), fields, { includeBestVisForTargetFields: true });
			}
			throw new Error('Invalid arguments!');
		};

		/**
		 * @param {string} dataSourceId active data source id
		 * @returns an array of all the field IDs
		 */
		this.getAllFieldIds = function(dataSourceId) {
			const allFields = startingPointsModel.fields.getFields(dataSourceId).getAll();
			return allFields.map(({ id }) => id);
		};
		/**
		 * @param {string} dataSourceId active data source id
		 * @returns an array of the interesting field IDs
		 */
		this.getInterestingFieldIds = function(dataSourceId) {
			const interestingFields = startingPointsModel.fields.getFields(dataSourceId).getInteresting();
			return interestingFields.map(({ id }) => id);
		};
		/**
		 * Function to satisfy wizard conditions by selecting the first field as focus field
		 * @param {string} dataSourceId active data source id
		 * @return the id of focus field selected
		 */
		this.skipWizard = function(dataSourceId) {
			const { all: { state, value: allFields } } =  startingPointsModel.fields.getFields(dataSourceId);
			if(state === 'fulfilled' && allFields.length) {
				//Usual case: select first field
				return this.setFocusField(allFields[0].id).then(this.getFocusField.bind(this));
			} else {
				throw createApiError('No focus field was set');
			}
		};

		/**
		 * @return the current focus field id
		 */
		this.getFocusField = function() {
			const focusField = startingPointsModel.fields.getFocusField();
			if(focusField) {
				return focusField.id;
			}
			return null;
		};

		/**
		 * @param {number} id - set the focus field to the field with this id
		 */
		this.setFocusField = function(id) {
			const fieldsPromise = startingPointsModel.fields.getFields().interesting;
			return fieldsPromise.then( () => {
				startingPointsModel.fields.setFocusField(id);
				const focusField = startingPointsModel.fields.getFocusField();
				if(!focusField || focusField.id !== id) {
					throw createApiError('Invalid id', 'STARTP_INVALID_FOCUS_FIELD_ID');
				}
			});
		};

		/**
		 * @deprecated field list is going away
		 * @param {number} index - set the focus field to the field that is at the given index of the interesting fields
		 */
		this.setFocusFieldIndex = function(index) {
			const fieldsPromise = startingPointsModel.fields.getFields().interesting;
			return fieldsPromise.then( () => {
				const interesting = startingPointsModel.fields.getFields().getInteresting() || [];
				if (!(index >= 0 && index < interesting.length)) {
					throw createApiError('Invalid index', 'STARTP_INVALID_FOCUS_FIELD_INDEX');
				}
				startingPointsModel.fields.setFocusFieldIndex(index);
			});
		};

		/**
		 * Add a field to relationship diagram
		 * @param {String[]} field ID list
		 * @return {boolean} True means the set action is executed, False otherwise
		 */
		this.addRelationshipDiagramFields = function(fields){
			if(fields && fields.length){
				const relationshipVM = startingPointsModel.relationship;
				const targetModel = relationshipVM && relationshipVM.getTargetModel();
				if(targetModel){
					targetModel.setLastAddedFields(fields);
					return true;
				}
			}
			return false;
		};

		/**
		 * Prevent fields from being shown in the relationship diagram
		 * @param {string[]} fields - array of field ids to remove
		 */
		this.removeRelationshipDiagramFields = function(fields) {
			fields = (fields || []).map(itemId => ({ itemId }));
			startingPointsModel.relationship.deleteFields(fields);
		};

		/**
		 * Controls the relationship type (primary/secondary) displayed in the relationship diagram
		 * @param {boolean} bValue - true/false
		 * @return {boolean} true when the API can set the input parameter value to the property 'showPrimaryRelationshipView'
		 * and false when the set doesn't happen
		 */
		this.setShowPrimaryRelationshipView = function(bValue) {
			const targetModel = startingPointsModel.relationship.getTargetModel();
			if (targetModel && targetModel.setShowPrimaryRelationshipView) {
				targetModel.setShowPrimaryRelationshipView(bValue);
				return true;
			} else {
				return false;
			}
		};

		/**
		 * Set the relationship strength range to filter relationships in the relationship diagram
		 * @param {Object} filterStrength - strength range (min, max) value to filter relationship strength
		 * @return {boolean} true/false - true when the set function happens and the property value is updated and false when the set doesn't happen.
		 */
		this.setRelationshipFilterStrength = function(filterStrength) {
			const targetModel = startingPointsModel.relationship.getTargetModel();
			if (targetModel && targetModel.setFilterStrength) {
				targetModel.setFilterStrength(filterStrength);
				return true;
			} else {
				return false;
			}
		};
		/**
		* Reset relationship diagram
		*/
		this.resetRelationshipDiagram = function() {
			startingPointsModel.relationship.reset();
		};

		/**
		 * Set the relationship diagram scope to given fields list
		 * @param {object[]} fields field id list
		 * @returns {boolean} true if successfully set scoped fields, false if not
		 */
		this.setScope = function(fields) {
			if (!(fields && fields.length)) {
				throw createApiError('Invalid fields list', 'STARTP_INVALID_SCOPE_FIELDS_LIST');
			}

			const targetModel = startingPointsModel.relationship.getTargetModel();
			if(targetModel) {
				targetModel.setScope(fields.map(parseFieldIDForExpression));
				return true;
			} else {
				return false;
			}
		};

		/**
		 * Show the relationship between `from` node and `to` node
		 * @param {string} from - field id
		 * @param {string} to - field id
		 */
		this.showRelationshipsBetween = function(from, to) {
			if (!(from && to && from.length && to.length)) {
				throw createApiError('Invalid `from` or `to` field id', 'STARTP_INVALID_FROM_TO_FIELD_ID');
			}

			const targetModel = startingPointsModel.relationship.getTargetModel();
			if(targetModel) {
				targetModel.showRelationshipsBetween(from, to);
				return true;
			} else {
				return false;
			}
		};

		/**
		 * Get Number Of RunningRequest
		 * @param {string} dataSourceId - defaut to current data source
		 * @return {number}
		 */
		this.getNumberOfRunningRequests = function(dataSourceId) {
			return startingPointsModel.relationship.getNumOfRunningRequests(dataSourceId);
		};

		/**
		 * If we can fetch data for the target Id. eg, the number of running requests are reached limit, we then cannot fetch data for a new target
		 * @param {string} targetId - targetId
		 * @param {string} action - `changeTarget` or `editScope`
		 * @return {boolean}
		 */
		this.canFetchData = function(targetId, action) {
			const result = startingPointsModel.relationship.canFetchData(targetId, action);
			return !!(result && result.canFetchData);
		};
	}
}



// WEBPACK FOOTER //
// ./src/js/api/StartingPointsAPI.js